import { supabase } from "@/lib/supabase";
import { USE_SUPABASE } from "@/lib/dataSource";
import { BienSchema, type BienRow, type BienUpdate } from "@/schemas/bien";
import { BienContactSchema, type BienContact } from "@/schemas/bienContact";
import { MOCK_BIENS, MOCK_TEAM } from "@/data/mock";
import { z } from "zod";

type BienInsert = { id: string } & Partial<Omit<BienRow, "id">>;

// Colonnes FK sur `biens` : Postgres refuse la chaîne vide, il faut NULL.
const FK_COLUMNS = ["prestataireEls", "quartier_id"] as const;
// Colonnes DATE : idem, "" → 22007 invalid input syntax, il faut NULL.
// `alerteTexte` (mig 023), `transfertElecDate` (mig 048) et `sousOptionDepuis`
// (mig 084) sont les seules VRAIES colonnes DATE de `biens` — les autres
// (edlDatePrevue, pebFinLe, renonRecuLe, etc.) sont TEXT et tolerent "" sans erreur.
const DATE_COLUMNS = ["alerteTexte", "transfertElecDate", "sousOptionDepuis"] as const;

function sanitizeFk<T extends Record<string, unknown>>(data: T): T {
  const out: Record<string, unknown> = { ...data };
  for (const col of FK_COLUMNS) {
    if (out[col] === "") out[col] = null;
  }
  for (const col of DATE_COLUMNS) {
    if (out[col] === "") out[col] = null;
  }
  return out as T;
}

/**
 * Repository des biens — abstrait la source de données (mocks vs Supabase).
 *
 * Signature :
 * - `list(role)` : liste tous les biens autorisés pour le rôle courant
 * - `get(role, id)` : détail d'un bien
 * - `update(role, id, data)` : UPDATE via la RPC `update_entity_with_perms`
 *   (filtre les colonnes éditables selon `field_permissions`)
 *
 * Mutations CREATE/DELETE sont admin-only côté RLS — on les ajoutera quand
 * on branchera la page de création de bien (Phase D).
 */

function fromMocks(): BienRow[] {
  // Les mocks sont des objets complets (Bien), compatibles avec BienRow
  // (qui accepte toutes les colonnes en optionnel).
  return MOCK_BIENS as unknown as BienRow[];
}

async function listFromSupabase(role: string): Promise<BienRow[]> {
  const { data, error } = await supabase.rpc("get_biens_for_role", { p_role: role });
  if (error) throw new Error(`listBiens: ${error.message}`);
  return z.array(BienSchema).parse(data ?? []);
}

async function getFromSupabase(role: string, id: string): Promise<BienRow | null> {
  const { data, error } = await supabase.rpc("get_bien_for_role", { p_role: role, p_id: id });
  if (error) throw new Error(`getBien(${id}): ${error.message}`);
  if (!data) return null;
  return BienSchema.parse(data);
}

async function updateOnSupabase(role: string, id: string, patch: BienUpdate): Promise<BienRow> {
  const { data, error } = await supabase.rpc("update_entity_with_perms", {
    p_table_name: "biens",
    p_role: role,
    p_id: id,
    p_data: sanitizeFk(patch),
  });
  if (error) throw new Error(`updateBien(${id}): ${error.message}`);
  return BienSchema.parse(data);
}

async function markAsPublishedOnSupabase(id: string): Promise<BienRow> {
  // Bump explicite de last_published_at via RPC SECURITY DEFINER (cf. 045).
  // La RPC update_entity_with_perms ne peut pas être utilisée car elle filtre
  // par field_permissions et `last_published_at` n'y est pas listé (champ
  // technique, pas éditable côté UI normale).
  const { data, error } = await supabase.rpc("mark_bien_as_published", { p_id: id });
  if (error) throw new Error(`markAsPublished(${id}): ${error.message}`);
  return BienSchema.parse(data);
}

async function deleteOnSupabase(role: string, id: string): Promise<void> {
  // RPC delete_bien (SECURITY DEFINER, cf. migration 030) — bypass RLS pour
  // que la suppression marche en mode test comme en mode auth.
  const { error } = await supabase.rpc("delete_bien", {
    p_role: role,
    p_id: id,
  });
  if (error) throw new Error(`deleteBien(${id}): ${error.message}`);
}

async function createOnSupabase(role: string, data: BienInsert): Promise<BienRow> {
  // Passe par la RPC `create_bien` (SECURITY DEFINER, cf. migration 029)
  // pour rester cohérent avec UPDATE/SELECT et fonctionner en mode test
  // (sans JWT côté DB, la RLS bloquerait un INSERT direct).
  const { data: row, error } = await supabase.rpc("create_bien", {
    p_role: role,
    p_data: sanitizeFk(data),
  });
  if (error) throw new Error(`createBien: ${error.message}`);
  return BienSchema.parse(row);
}

async function getContactsFromSupabase(bienId: string): Promise<BienContact[]> {
  const { data, error } = await supabase.rpc("get_bien_contacts", { p_bien_id: bienId });
  if (error) throw new Error(`getBienContacts(${bienId}): ${error.message}`);
  return z.array(BienContactSchema).parse(data ?? []);
}

export const biensRepository = {
  async list(role: string): Promise<BienRow[]> {
    if (!USE_SUPABASE) return fromMocks();
    return listFromSupabase(role);
  },

  async get(role: string, id: string): Promise<BienRow | null> {
    if (!USE_SUPABASE) {
      return (fromMocks().find((b) => b.id === id) ?? null);
    }
    return getFromSupabase(role, id);
  },

  async create(role: string, data: BienInsert): Promise<BienRow> {
    if (!USE_SUPABASE) {
      // Mutation in-place pour que le nouveau bien survive au refetch (demo local)
      (MOCK_BIENS as unknown as BienRow[]).push(data as BienRow);
      return data as BienRow;
    }
    return createOnSupabase(role, data);
  },

  async update(role: string, id: string, patch: BienUpdate): Promise<BienRow> {
    if (!USE_SUPABASE) {
      // Mutation in-place pour que l'update survive au refetch (demo local).
      // Sans ça, la liste refetch les mocks d'origine et le changement
      // (meublé, statut publication, etc.) n'apparait pas dans les filtres.
      const arr = MOCK_BIENS as unknown as BienRow[];
      const idx = arr.findIndex((b) => b.id === id);
      if (idx === -1) throw new Error(`Bien introuvable: ${id}`);
      arr[idx] = { ...arr[idx], ...patch };
      return arr[idx];
    }
    return updateOnSupabase(role, id, patch);
  },

  async delete(role: string, id: string): Promise<void> {
    if (!USE_SUPABASE) {
      const arr = MOCK_BIENS as unknown as BienRow[];
      const idx = arr.findIndex((b) => b.id === id);
      if (idx !== -1) arr.splice(idx, 1);
      return;
    }
    return deleteOnSupabase(role, id);
  },

  async markAsPublished(id: string): Promise<BienRow> {
    if (!USE_SUPABASE) {
      // Mode test : bump in-place dans les mocks. last_published_at = ISO now.
      const arr = MOCK_BIENS as unknown as BienRow[];
      const idx = arr.findIndex((b) => b.id === id);
      if (idx === -1) throw new Error(`Bien introuvable: ${id}`);
      arr[idx] = { ...arr[idx], last_published_at: new Date().toISOString() } as BienRow;
      return arr[idx];
    }
    return markAsPublishedOnSupabase(id);
  },

  /**
   * Contact (email/téléphone) des 3 personnes liées à ce bien
   * (collaborateurSrs/expertPeb/prestataireEle) — jamais toute la table
   * `profiles` (migration 126, correction du constat #3 de l'audit
   * 2026-09-10 : `get_profiles_for_role` exposait email/téléphone de tout le
   * monde à n'importe quel rôle authentifié).
   */
  async getContacts(bienId: string): Promise<BienContact[]> {
    if (!USE_SUPABASE) {
      const bien = (fromMocks() as unknown as Record<string, unknown>[]).find((b) => b.id === bienId);
      if (!bien) return [];
      const ids = [bien.collaborateurSrs, bien.expertPeb, bien.prestataireEle].filter(
        (v): v is string => typeof v === "string" && v.length > 0,
      );
      return MOCK_TEAM.filter((m) => ids.includes(m.id)).map((m) => ({
        id: m.id,
        first_name: m.prenom,
        last_name: m.nom,
        email: m.email,
        telephone: m.telephone,
      }));
    }
    return getContactsFromSupabase(bienId);
  },
};
